import { supabase } from './supabase';
import type { ActivitySubjectThreadItem } from '@shared/types/activity-feed';
import { isViewType, type AnyViewEnvelope } from '@shared/types/views';
import { sweepActivitySummary, sweepSerializedBusinessText } from './ui-editorial-sweeper';

export type WorkHistoryCursor = { at: string; key: string };
const record = (value: unknown): Record<string, unknown> => value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string,unknown> : {};
const text = (value: unknown): string | null => typeof value === 'string' ? value : null;
const envelope = (value: unknown): AnyViewEnvelope | null => {
  const candidate = record(value);
  return typeof candidate.view_type === 'string' && isViewType(candidate.view_type) && candidate.data && typeof candidate.data === 'object' ? candidate as unknown as AnyViewEnvelope : null;
};
const taskResultItem = (source: Record<string, unknown>): ActivitySubjectThreadItem => {
  const view = envelope(source.view_payload);
  const rawTitle = text(record(view?.data).title) ?? text(source.recommendation_label) ?? 'Note de suivi';
  return {
    id: `task-action:${String(source.id)}`, kind: 'event', title: sweepSerializedBusinessText(rawTitle),
    summary: sweepActivitySummary(text(source.result_summary), false),
    eventAt: text(source.completed_at) ?? text(source.created_at), actorLabel: 'Vous', tone: 'neutral',
    toolKey: text(source.capability_key), toolLabel: null, sourceRunId: text(source.source_run_id),
    taskActionRunId: text(source.id), primaryAction: null, resultView: view,
  };
};
export const readFocusedTaskActionResult = async (subjectId: string, runId: string): Promise<ActivitySubjectThreadItem | null> => {
  const { data, error } = await supabase.from('task_action_runs')
    .select('id,subject_id,account_subject_id,status,view_payload,recommendation_label,result_summary,completed_at,created_at,capability_key,source_run_id')
    .eq('id', runId).maybeSingle();
  if (error) throw new Error('Le résultat demandé n’a pas pu être chargé.');
  const source = record(data);
  if (source.status !== 'completed' || (text(source.account_subject_id) ?? text(source.subject_id)) !== subjectId) return null;
  return taskResultItem(source);
};
export const readWorkHistory = async (subjectId: string, cursor: WorkHistoryCursor | null) => {
  const {data,error} = await supabase.rpc('read_work_history', {p_subject_id:subjectId,p_before_at:cursor?.at ?? null,p_before_key:cursor?.key ?? null,p_limit:21});
  if (error) throw new Error('L’historique du compte n’a pas pu être chargé.');
  if (!Array.isArray(data)) throw new Error('L’historique reçu est incomplet.');
  const rows = data.slice(0,20).map(record);
  const items: ActivitySubjectThreadItem[] = rows.map(row => {
    const source = record(row.source);
    const task = row.source_kind === 'task_action';
    if (task) return taskResultItem(source);
    const view = envelope(row.flow_view);
    const rawTitle = text(record(view?.data).title) ?? text(source.outcome_summary) ?? 'Note de suivi';
    const title = sweepSerializedBusinessText(rawTitle);
    const summary = sweepActivitySummary(text(source.outcome_summary), source.outcome_status === 'failed');
    return {id:String(row.event_key),kind:'run',title,summary,eventAt:text(row.event_at),actorLabel:text(source.actor_label),tone:'neutral',toolKey:text(source.source_tool_key),toolLabel:null,sourceRunId:text(source.source_run_id),taskActionRunId:null,primaryAction:null,resultView:view};
  });
  const last = rows.at(-1);
  return {items,nextCursor:data.length>20 && last ? {at:String(last.event_at),key:String(last.event_key)}:null};
};
